import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { AuthService } from '@core/authentication/auth.service';

// Authentication-only gate for the whole /v2/home tree.
//
// /v2/home deliberately does NOT use the role-based `authGuardFn`: that guard's per-role
// access lists don't include 'home', so it would deny-access logged-in non-admin roles.
// The role-specific landing/redirect guards (homeRoleRedirectGuard, dashboardAccessGuard,
// dashboardLandingGuard, dashboardLegacyRouteGuard) all treat a missing user as "allow" —
// they only decide *where a role lands*, not *whether you're logged in*.
//
// The result was that an anonymous user hitting '/' (redirected via the retired legacy
// dashboard to '/v2/home/dashboard', which bypasses the app-level authGuardFn) fell through
// every guard and landed on a blank legacy-dashboard embed. This guard closes that gap by
// bouncing unauthenticated users to /login, preserving the attempted URL as returnUrl.
export const homeAuthenticatedGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): boolean | UrlTree => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.getUser()) {
    return true;
  }

  return router.createUrlTree(['/login'], {
    queryParams: { returnUrl: state.url },
  });
};
